import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import { SourcePane } from './SourcePane';
import { DestinationPane } from './DestinationPane';
import CourseLinkageDialog from './CourseLinkageDialog';
import { moodleApi } from '../services/moodle-api';
import { environmentApi } from '../services/api';
import { useJobs } from '../contexts/JobsContext';

export type SourceType = 'moodle' | 's3';
export type ContentType = 'courses' | 'competency_frameworks' | 'learning_path_templates' | 'categories';

export interface SourceSelection {
  type: SourceType;
  instanceId: string; // Moodle source ID or S3 environment ID
  instanceName?: string; // Display name for Moodle server or S3 environment
  environmentPrefix?: string; // S3 environment prefix (for S3 sources only)
  contentType: ContentType;
  selectedItems: string[]; // IDs or file keys
  displayCount?: number; // Optional: number of items to display (may differ from selectedItems.length for grouped items)
  selectedVersions?: Record<string, string>; // Map of item key to selected version (e.g., "v1", "v2")
  selectedCmi5ActivityVersions?: Record<string, Record<string, string>>; // Map of course key to activity key to version
}

export interface DestinationSelection {
  type: SourceType;
  instanceId: string; // Moodle source ID or S3 environment ID
  instanceName?: string; // Display name for Moodle server or S3 environment
  environmentPrefix?: string; // S3 environment prefix (for S3 destinations only)
  targetFolder?: string; // For S3
  targetCategoryId?: number; // For Moodle
  customNames?: Record<string, string>; // Custom names for duplicate items
}

interface TransferManagerProps {
  currentEnvironment: string;
  onClose?: () => void;
  onBackToS3Manager?: () => void;
}

export const TransferManager: React.FC<TransferManagerProps> = ({ currentEnvironment, onClose, onBackToS3Manager }) => {
  const [sourceSelection, setSourceSelection] = useState<SourceSelection | null>(null);
  const [sourceItems, setSourceItems] = useState<any[]>([]); // All items from source for comparison
  const [destinationSelection, setDestinationSelection] = useState<DestinationSelection | null>(null);
  const [destinationItems, setDestinationItems] = useState<any[]>([]); // All items from destination for comparison
  const [transferring, setTransferring] = useState(false);
  const [transferProgress, setTransferProgress] = useState<{ current: number; total: number; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [linkageDialogOpen, setLinkageDialogOpen] = useState(false);
  const [selectedCourseForLinkage, setSelectedCourseForLinkage] = useState<{ filename: string; name?: string } | null>(null);

  const { refreshJobs, setShowJobsPanel} = useJobs();

  // Debug: log when sourceSelection changes
  React.useEffect(() => {
    console.log('[TransferManager] sourceSelection changed:', sourceSelection);
  }, [sourceSelection]);

  // Memoize callbacks to prevent infinite loops
  const handleSourceSelectionChange = React.useCallback((selection: SourceSelection | null) => {
    console.log('[TransferManager] Source selection changed:', selection);
    setSourceSelection(selection);
  }, []);

  const handleDestinationSelectionChange = React.useCallback((selection: DestinationSelection | null) => {
    console.log('[TransferManager] Destination selection changed:', selection);
    setDestinationSelection(selection);
  }, []);

  const handleTransferClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleTransfer = async () => {
    if (!sourceSelection || !destinationSelection) {
      return;
    }

    setConfirmDialogOpen(false);
    setError(null);
    setSuccessMessage(null);

    try {
      const { type: sourceType, instanceId: sourceId, contentType, selectedItems } = sourceSelection;
      const { type: destType, instanceId: destId, targetFolder, targetCategoryId } = destinationSelection;

      // All Moodle-related transfers now create jobs
      if (sourceType === 'moodle' && destType === 's3') {
        // Moodle → S3: Export from Moodle to S3
        await handleMoodleToS3Transfer(sourceId, destId, contentType, selectedItems);
        // Multiple refresh attempts to ensure job appears
        await refreshJobs();
        await new Promise(resolve => setTimeout(resolve, 300));
        await refreshJobs();
        setShowJobsPanel(true); // Auto-open jobs panel
        setSuccessMessage('Export job created! Check the Jobs panel for progress.');
      } else if (sourceType === 's3' && destType === 'moodle') {
        // S3 → Moodle: Import from S3 to Moodle (now with job support for courses)
        await handleS3ToMoodleTransfer(sourceId, destId, contentType, selectedItems, targetCategoryId || 1);
        if (contentType === 'courses') {
          // Single refresh is enough now that syncJobs merges instead of replaces
          await new Promise(resolve => setTimeout(resolve, 200));
          await refreshJobs();
          setShowJobsPanel(true); // Auto-open jobs panel
          setSuccessMessage('Import job created! Check the Jobs panel for progress.');
        } else {
          setSuccessMessage('Import completed successfully!');
        }
      } else if (sourceType === 'moodle' && destType === 'moodle') {
        // Moodle → Moodle: Direct transfer between Moodle instances (now with job support)
        await handleMoodleToMoodleTransfer(sourceId, destId, contentType, selectedItems, targetCategoryId || 1);
        // Single refresh is enough now that syncJobs merges instead of replaces
        await new Promise(resolve => setTimeout(resolve, 200));
        await refreshJobs();
        setShowJobsPanel(true); // Auto-open jobs panel
        setSuccessMessage('Transfer job created! Check the Jobs panel for progress.');
      } else if (sourceType === 's3' && destType === 's3') {
        // S3 → S3: Copy between S3 environments (with job support)
        await handleS3ToS3Transfer(sourceId, destId, targetFolder!, selectedItems);
        // Single refresh is enough now that syncJobs merges instead of replaces
        await new Promise(resolve => setTimeout(resolve, 200));
        await refreshJobs();
        setShowJobsPanel(true); // Auto-open jobs panel
        setSuccessMessage('Transfer job created! Check the Jobs panel for progress.');
      }
      
      // Clear selections after initiating transfer
      setSourceSelection(null);
      setDestinationSelection(null);
    } catch (error) {
      console.error('Transfer failed:', error);
      setError(error instanceof Error ? error.message : 'Transfer failed');
      setTransferring(false);
    }
  };

  const handleMoodleToS3Transfer = async (
    moodleSourceId: string,
    s3EnvironmentId: string,
    contentType: ContentType,
    itemIds: string[]
  ) => {
    // These already create jobs on the server, just initiate them
    // Progress tracking happens through the job system
    switch (contentType) {
      case 'courses':
        await moodleApi.exportCoursesWithProgress(
          moodleSourceId,
          itemIds.map(id => parseInt(id)),
          s3EnvironmentId,
          () => {} // Job handles progress
        );
        break;
      case 'competency_frameworks':
        await moodleApi.exportCompetencyFrameworksWithProgress(
          moodleSourceId,
          itemIds.map(id => parseInt(id)),
          s3EnvironmentId,
          () => {} // Job handles progress
        );
        break;
      case 'learning_path_templates':
        await moodleApi.exportLearningPathTemplatesWithProgress(
          moodleSourceId,
          itemIds.map(id => parseInt(id)),
          s3EnvironmentId,
          () => {} // Job handles progress
        );
        break;
      case 'categories':
        await moodleApi.exportCategoriesWithProgress(
          moodleSourceId,
          itemIds.map(id => parseInt(id)),
          s3EnvironmentId,
          () => {} // Job handles progress
        );
        break;
      default:
        throw new Error(`Export not supported for content type: ${contentType}`);
    }
  };

  const handleS3ToMoodleTransfer = async (
    s3EnvironmentId: string,
    moodleTargetId: string,
    contentType: ContentType,
    s3Keys: string[],
    categoryId: number
  ) => {
    // Course imports now use job system
    if (contentType === 'courses') {
      await moodleApi.importCoursesFromS3WithProgress(
        moodleTargetId,
        s3Keys,
        categoryId,
        s3EnvironmentId
      );
    } else {
      // Other content types still run inline with local progress
      setTransferring(true);
      setTransferProgress({ current: 0, total: s3Keys.length, message: 'Starting import...' });

      try {
        switch (contentType) {
          case 'competency_frameworks':
            await moodleApi.importCompetencyFrameworksFromS3(moodleTargetId, s3Keys, s3EnvironmentId);
            break;
          case 'learning_path_templates':
            await moodleApi.importLearningPathTemplatesFromS3(moodleTargetId, s3Keys, s3EnvironmentId);
            break;
          case 'categories':
            if (s3Keys.length > 0) {
              await moodleApi.importCategoriesFromS3(moodleTargetId, s3Keys[0], s3EnvironmentId);
            }
            break;
          default:
            throw new Error(`Import not supported for content type: ${contentType}`);
        }
      } finally {
        setTransferring(false);
        setTransferProgress(null);
      }
    }
  };

  const handleMoodleToMoodleTransfer = async (
    sourceMoodleId: string,
    targetMoodleId: string,
    contentType: ContentType,
    itemIds: string[],
    categoryId: number
  ) => {
    if (contentType !== 'courses') {
      throw new Error('Only course transfers are supported between Moodle instances');
    }

    // Now uses job system
    await moodleApi.importCoursesFromMoodleWithProgress(
      sourceMoodleId,
      targetMoodleId,
      itemIds.map(id => parseInt(id)),
      categoryId
    );
  };

  const handleS3ToS3Transfer = async (
    sourceEnvironmentId: string,
    targetEnvironmentId: string,
    targetFolder: string,
    fileKeys: string[]
  ) => {
    // Use job-based approach for better progress tracking
    // Pass full S3 keys to preserve versioned file locations
    // The backend will handle extracting filenames and checking versioned vs legacy paths
    const selections = {
      [targetFolder]: fileKeys,
    };

    // Use environment IDs to ensure correct bucket selection
    await environmentApi.cloneEnvironmentWithJob(sourceEnvironmentId, targetEnvironmentId, selections);
  };

  const canTransfer = sourceSelection &&
                     destinationSelection &&
                     sourceSelection.selectedItems.length > 0 &&
                     !transferring;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header */}
      <Paper sx={{ px: 2, py: 1, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBackToS3Manager && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBackToS3Manager}
              variant="outlined"
              size="small"
            >
              Back to S3 Manager
            </Button>
          )}
          <Box>
            <Typography variant="h6">
              Transfer Manager
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select source and destination independently - transfer content between any Moodle or S3 environment
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* View Linkages Button - Only show for S3→Moodle course imports */}
          {sourceSelection?.type === 's3' &&
           sourceSelection?.contentType === 'courses' &&
           destinationSelection?.type === 'moodle' &&
           sourceSelection?.selectedItems.length === 1 && (
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={() => {
                const courseKey = sourceSelection.selectedItems[0];
                setSelectedCourseForLinkage({ filename: courseKey });
                setLinkageDialogOpen(true);
              }}
            >
              View Linkages
            </Button>
          )}

          {/* Always visible Transfer Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleTransferClick}
            disabled={!canTransfer}
            startIcon={transferring ? <CircularProgress size={18} /> : <SwapHorizIcon />}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {transferring ? 'Working...' : `Transfer ${sourceSelection?.selectedItems.length || 0}`}
          </Button>
          {onClose && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Error Banner */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 0, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Compact Progress Bar */}
      {transferring && transferProgress && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
          <Typography variant="caption" display="block" gutterBottom>
            {transferProgress.message}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(transferProgress.current / transferProgress.total) * 100}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary">
            {transferProgress.current} / {transferProgress.total}
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Source Pane */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography variant="subtitle1" fontWeight="bold">Source</Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <SourcePane
              currentEnvironment={currentEnvironment}
              onSelectionChange={handleSourceSelectionChange}
              onItemsChange={setSourceItems}
              destinationItems={destinationItems}
            />
          </Box>
        </Box>

        {/* Destination Pane */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderLeft: 1, borderColor: 'divider' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
            <Typography variant="subtitle1" fontWeight="bold">Destination</Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <DestinationPane
              currentEnvironment={currentEnvironment}
              sourceContentType={sourceSelection?.contentType}
              sourceItems={sourceItems}
              selectedSourceItems={sourceSelection?.selectedItems || []}
              onSelectionChange={handleDestinationSelectionChange}
              onItemsChange={setDestinationItems}
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Transfer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to transfer <strong>{sourceSelection?.displayCount ?? sourceSelection?.selectedItems.length}</strong>{' '}
            {sourceSelection?.contentType.replace(/_/g, ' ')} from{' '}
            <strong>{sourceSelection?.type === 'moodle' ? 'Moodle' : 'S3'}</strong> ({sourceSelection?.instanceName || 'Unknown'}) to{' '}
            <strong>{destinationSelection?.type === 'moodle' ? 'Moodle' : 'S3'}</strong> ({destinationSelection?.instanceName || 'Unknown'}).
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            {(sourceSelection?.type === 'moodle' || 
              (sourceSelection?.type === 's3' && destinationSelection?.type === 'moodle' && sourceSelection?.contentType === 'courses')) ? (
              <>This will create a background job. You can monitor progress in the Jobs panel.</>
            ) : (
              <>This operation may take several minutes and will run in this window.</>
            )}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontStyle: 'italic', fontSize: '0.9em' }}>
            Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleTransfer} variant="contained" autoFocus>
            {(sourceSelection?.type === 'moodle' ||
              (sourceSelection?.type === 's3' && destinationSelection?.type === 'moodle' && sourceSelection?.contentType === 'courses') ||
              (sourceSelection?.type === 's3' && destinationSelection?.type === 's3'))
              ? 'Create Job' : 'Start Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />

      {/* Course Linkage Dialog */}
      {selectedCourseForLinkage && sourceSelection?.environmentId && (
        <CourseLinkageDialog
          open={linkageDialogOpen}
          onClose={() => {
            setLinkageDialogOpen(false);
            setSelectedCourseForLinkage(null);
          }}
          bucketId={sourceSelection.bucketId || ''}
          environmentId={sourceSelection.environmentId}
          courseFilename={selectedCourseForLinkage.filename}
          courseName={selectedCourseForLinkage.name}
        />
      )}
    </Box>
  );
};
